import React, { Fragment, useState } from 'react'
import { withRouter, Link } from 'react-router-dom'
import { flowRight as compose } from 'lodash'
import { withApollo } from '@apollo/client/react/hoc'
import PropTypes from 'prop-types'
import styled from '@emotion/styled/macro'

import { Icon, LoadingSpinner, Button } from '@paypalcorp/pp-react'

import { isDevOrStageHostname } from '../../utils/helpers'
import { SearchInput } from '../../components/search-bar'
import NoSearchResults from './NoSearchResults'

const ProductSection = styled.div`
  padding-top: 42px;
  display: flex;
  flex-direction: column;
`

const ProductSectionSpinner = styled.div`
  margin-left: auto;
  margin-right: auto;
`

const SectionHeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  width: 852px;

  @media only screen and (max-width: 900px) {
    margin: auto 24px 24px;
    width: auto;
    align-items: flex-start;
  }
`

const SectionTitle = styled.div`
  color: #212b36;
  font-family: 'PayPalSansBig-Medium', Helvetica Neue, Arial, sans-serif;
  font-size: 24px;
  font-weight: 500;
  line-height: 30px;
  margin-bottom: 10px;
`

const SectionSubTitle = styled.div`
  color: #2C2E2F;
  font-family: "PayPalSansBig-Regular", Helvetica Neue, Arial, sans-serif'
  font-size: 14px;
  font-weight: 500;
  line-height: 24px;
  display: inline;
`

const SectionSubtitleLocation = styled.div`
  display: inline;
`

const DesktopButtonContainer = styled.div`
  @media only screen and (max-width: 900px) {
    display: none !important;
  }
`

const MobileButtonLink = styled.a`
  color: #0070ba !important;
  cursor: pointer;

  @media only screen and (min-width: 901px) {
    display: none !important;
  }
`

const ProductDisplay = styled.div`
  display: flex;
  flex-wrap: wrap;

  @media only screen and (max-width: 900px) {
    margin: auto 24px;
  }
`

const Product = styled.div`
  width: 254px;
  margin-bottom: 30px;
  margin-right: 30px;
`

const ProductImage = styled.div`
  width: 254px;
  height: 160px;
  border-radius: 6px;
  background-color: #ffffff;
  box-shadow: 0 4px 2px 0 rgba(0, 0, 0, 0.1);
`

const Img = styled.img`
  width: 254px;
  height: 160px;
  border-radius: 6px;
`

const ImgCaption = styled.div`
  color: #2c2e2f;
  font-family: 'PayPalSansBig-Regular', Helvetica Neue, Arial, sans-serif;
  font-size: 14px;
  line-height: 24px;
  margin-top: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ImgCaptionLine2 = styled.div`
  color: #687173;
`

const IconImgContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const IconImg = styled.img`
  height: 94px;
  width: 94px;
  border: 1px solid #eaeced;
  border-radius: 50%;
  margin-bottom: 16px;
`

const IconImgCaption = styled.div`
  color: #2c2e2f;
  font-family: 'PayPalSansBig-Regular', Helvetica Neue, Arial, sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  text-align: center;
`

const EditLocationLink = styled.a`
  cursor: pointer;
`

export const LocationBasedProductSection = ({
  basepath,
  history,
  title,
  subtitle,
  buttonContent,
  locationName,
  productList,
  displayNameAndAddressCaption,
  editableLocation,
  onClickButton,
  editableSearchLabel,
  editableSearchButtonLabel,
  noSearchResultsTitle,
  noSearchResultsSubtitle,
  noSearchResultsLabel,
  onSaveEditLocation,
  showLoadingSpinner,
  redirectToSearchRouteOnNull,
  currentLocationLabel,
}) => {
  const [isEditingLocation, setIsEditingLocation] = useState(false)
  const DEFAULT_VOUCHER_IMAGE_URL = `https://www.paypalobjects.com/ppcp/images/covid/donations@x600.png`

  const onClickEditLocation = () => {
    setIsEditingLocation(true)
  }

  const renderEditableSubtitle = () => {
    const Text = () => <Fragment>&nbsp;{locationName.trim()}</Fragment>
    if (!locationName.trim()) return null
    if (!editableLocation) return <Text />
    return (
      <EditLocationLink onClick={onClickEditLocation}>
        <Text />
        <Icon name="edit" size="xs" />
      </EditLocationLink>
    )
  }

  const onLocationUpdate = (value) => {
    if (value) onSaveEditLocation(value)
    setIsEditingLocation(false)
  }

  const getDonationLink = (sku) => {
    let baseUrl = 'https://www.paypal.com/biz/fund?id='
    if (isDevOrStageHostname()) {
      baseUrl =
        'https://www.msmaster.qa.paypal.com/cgi-bin/webscr?cmd=_s-xclick&hosted_button_id='
    }
    return `${baseUrl}${sku}`
  }

  const renderProductLocation = (product) => {
    if (!product.address) return null

    const { city, country, line1, line2, postalCode, state } = product.address

    const padder = (s, pad = ' ') => (s && `${s}${pad}`) || ''
    const address = `${padder(city, ', ')}${padder(state)}${padder(
      country,
    )}${padder(postalCode, '')}`.trim()

    return (
      <Fragment>
        <ImgCaptionLine2>{address}</ImgCaptionLine2>
        {line1 && <ImgCaptionLine2>{line1}</ImgCaptionLine2>}
        {line2 && <ImgCaptionLine2>{line2}</ImgCaptionLine2>}
      </Fragment>
    )
  }

  if (!productList || productList.length === 0) {
    if (redirectToSearchRouteOnNull) {
      if (!locationName) return ''
      history.push(`/business/search/?q=${encodeURIComponent(locationName)}`)
    }
    return (
      <NoSearchResults
        title={noSearchResultsTitle}
        subtitle={noSearchResultsSubtitle}
        searchLabel={noSearchResultsLabel}
        onSubmit={onSaveEditLocation}
        enableSeeYourLocation={true}
        currentLocationLabel={currentLocationLabel}
      />
    )
  }

  const renderProduct = (product) => (
    <Product key={product.id}>
      <ProductImage>
        {product.sku ? (
          <IconImgContainer>
            <IconImg
              src={product.img_url || DEFAULT_VOUCHER_IMAGE_URL}
              alt={`${product.categories[0]} ${product.name}`}
            />
            <IconImgCaption>{product.name}</IconImgCaption>
          </IconImgContainer>
        ) : (
          <Img
            src={product.img_url}
            alt={`${product.categories[0]} ${product.name}`}
          />
        )}
      </ProductImage>
      {displayNameAndAddressCaption && (
        <ImgCaption>
          {product.name}
          {renderProductLocation(product)}
        </ImgCaption>
      )}
    </Product>
  )

  return (
    <Fragment>
      <ProductSection>
        <SectionHeaderContainer>
          <div>
            <SectionTitle>{title}</SectionTitle>
            {isEditingLocation ? (
              <SearchInput
                placeholder={editableSearchLabel}
                inNav={false}
                submitLabel={editableSearchButtonLabel}
                onSubmit={onLocationUpdate}
                enableSeeYourLocation={true}
                currentLocationLabel={currentLocationLabel}
              />
            ) : (
              <SectionSubTitle>
                {subtitle.trim()}
                <SectionSubtitleLocation>
                  {renderEditableSubtitle()}
                </SectionSubtitleLocation>
              </SectionSubTitle>
            )}
          </div>
          {!editableLocation && buttonContent && (
            <div>
              <DesktopButtonContainer>
                <Button secondary size="md" onClick={onClickButton}>
                  {buttonContent}
                </Button>
              </DesktopButtonContainer>
              <MobileButtonLink onClick={onClickButton}>
                {buttonContent}
              </MobileButtonLink>
            </div>
          )}
        </SectionHeaderContainer>
        <ProductDisplay>
          {productList.map((product, idx) =>
            product.sku ? (
              <a
                key={`product-list-${idx}`}
                href={getDonationLink(product.sku)}
                target="_blank"
                rel="noreferrer"
              >
                {renderProduct(product)}
              </a>
            ) : (
              <Link
                key={`product-list-${idx}`}
                to={`${basepath || ''}${product.url_key}`}
              >
                {renderProduct(product)}
              </Link>
            ),
          )}
        </ProductDisplay>
        {showLoadingSpinner && (
          <ProductSectionSpinner>
            <LoadingSpinner />
          </ProductSectionSpinner>
        )}
      </ProductSection>
    </Fragment>
  )
}

LocationBasedProductSection.propTypes = {
  basepath: PropTypes.string,
  history: PropTypes.object,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  buttonContent: PropTypes.element,
  locationName: PropTypes.string,
  productList: PropTypes.array,
  displayNameAndAddressCaption: PropTypes.bool,
  editableLocation: PropTypes.bool,
  onClickButton: PropTypes.func,
  editableSearchLabel: PropTypes.string,
  editableSearchButtonLabel: PropTypes.string,
  noSearchResultsTitle: PropTypes.string,
  noSearchResultsSubtitle: PropTypes.string,
  noSearchResultsLabel: PropTypes.string,
  onSaveEditLocation: PropTypes.func,
  showLoadingSpinner: PropTypes.bool,
  redirectToSearchRouteOnNull: PropTypes.bool,
  currentLocationLabel: PropTypes.string,
}

const LocationBasedProductSectionWithGraphql = compose(withApollo)(
  withRouter(LocationBasedProductSection),
)

export default LocationBasedProductSectionWithGraphql
